import type { Dispatch } from 'redux';
import type {
    TranscriptData as LuminaryTranscriptData,
    TranscriptWordData,
    Scene,
    SceneGroup,
} from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';
import { createHeadlessEditor } from '@lexical/headless';
import { $dfsIterator, $getNearestNodeOfType } from '@lexical/utils';
import type { EditorState, LexicalEditor, SerializedEditorState } from 'lexical';
import { $getRoot, $nodesOfType } from 'lexical';

import type {
    AudioAssetModel,
    AudioAssetSourceData,
    EditedTranscriptWord,
    EditedTranscriptWordRequest,
    LanguageLocalesEnum,
    TranscriptWord,
} from 'js/src/client';
import {
    ApiService,
    AsyncOperationMessageTypeEnum,
    AsyncOperationNameEnum,
    LanguageEnum,
} from 'js/src/client';
import { Toast } from 'js/src/components/common/Toast';
import { syncGenxClipPlaybackRatesOperation } from 'js/src/data/animated-explainer-videos/genx-clip-playback-rate';
import {
    getVideoId,
    markSceneGroupAsFinishedLoading,
    markSceneGroupAsLoading,
    SCENE_GROUP_LOADING_TYPES,
} from 'js/src/data/app';
import StoreORM from 'js/src/data/store-orm';
import {
    disableTranscriptOverlays,
    endLexicalControllingItsOwnState,
    endTimingMode,
    overwriteAllLexicalEditors,
    setSceneConversionModalVisible,
    setTranscriptCurrentTime,
    setTranscriptLanguage,
    setTranscriptTabVisibility,
    startLexicalControllingItsOwnState,
    startTimingMode,
    storeLexicalEditor,
    storeRawTranscript,
    storeTranscriptTranslations,
} from 'js/src/data/transcript/transcript-actions';
import {
    getEditorFromSceneGroupId,
    getLexicalEditors,
    getOriginalTranscriptLanguage,
    getTimingModeSceneGroupId,
    getTranscriptById,
    getTranscripts,
    getTranscriptTranslationLanguage,
    isTimingModeActive,
} from 'js/src/data/transcript/transcript-selectors';
import type {
    InterestPointTime,
    LexicalEditorsDataType,
    SerializedTranscript,
    TranscriptData,
    TranscriptTranslations,
} from 'js/src/data/transcript/transcript-types';
import {
    ENTER_TIMING_MODE_COMMAND,
    EXIT_TIMING_MODE_COMMAND,
    InterestPointTimeType,
    TypeOfTranscript,
    VALIDATE_TRANSCRIPT_COMMAND,
} from 'js/src/data/transcript/transcript-types';
import {
    $generateEditorFromTranscript,
    SilenceNodeGenerationMethod,
} from 'js/src/data/transcript/generate-editor-from-transcript';
import { BaseOverlayElementNode } from 'js/src/data/transcript/nodes/BaseOverlayElementNode';
import { OverlayElementNode, OverlayState } from 'js/src/data/transcript/nodes/OverlayElementNode';
import type { TranscriptWordNode } from 'js/src/data/transcript/nodes/TranscriptWordNode';
import { $isTranscriptWordNode } from 'js/src/data/transcript/nodes/TranscriptWordNode';
import { $isTrimElementNode } from 'js/src/data/transcript/nodes/TrimElementNode';
import { wordGapMidpoint } from 'js/src/data/transcript/word-timing';
import type { FirstAndLastWordIdsBySceneId } from 'js/src/data/transcript/transcript-utils';
import {
    $findMatchWithin,
    $getFineOverlayBoundaryTimeCloseTo,
    $getFirstLastWordIdsBySceneId,
    $getLastNonEmptyWordWithin,
    $getNextTranscriptWordNode,
    $getNextWordAtTime,
    $getNodeTimeBounds,
    $getOverlaySceneIdImmediatelyAfter,
    $getOverlaySceneIdImmediatelyBefore,
    $getPreviousTranscriptWordNode,
    $getPreviousWordAtTime,
    $getSelectedNodes,
    $getSilenceSpans,
    $getUntrimmedFillerWords,
    $getWordNodeByWordId,
    $getWordNodesBetweenTimes,
    createLexicalEditor,
    getFullTranscriptFromEditor,
    getNextTranslationEditorName,
    getTranscriptDuration,
    getTranscriptWords,
    theseAreOverlapping,
    thisIsWithinRange,
    TRANSCRIPT_EDITOR_CONFIG,
    updateEditor,
} from 'js/src/data/transcript/transcript-utils';
import {
    getTranslatedEditorBySceneGroupId,
    removeTranslatedEditor,
    storeTranslatedEditor,
    storeTranslatedEditors,
} from 'js/src/data/translated-editors';
import { reduceTranscriptsToTranslationEditorsMap } from 'js/src/data/translated-editors/translated-editors.utils';
import {
    getLanguageTextDirection,
    getSupportedLanguages,
    shouldSeparateTranscriptWordsWithSpaces,
    shouldSeparateWordsWithSpaces,
    translateText,
} from 'js/src/data/translations';
import { convertLocaleToLanguageCode } from 'js/src/data/translations/utils';
import { getAssetsWithTimesOverridden } from 'js/src/data/user-overrides/user-overrides-selectors';
import {
    checkpointVideo,
    resetLuminaryAndLexicalToPresentCheckpointOperation,
} from 'js/src/data/video-data';
import { BatchAnalytics } from 'js/src/libs/BatchAnalytics';
import type { ImmutableMap } from 'js/src/libs/consts';
import { ImmutableFromJS, LOADING_SCENE_MSG } from 'js/src/libs/consts';
import type { AppDispatch, GetRootState } from 'js/src/libs/RootState';
import type { StartScenePreviewPlaybackPayload } from 'js/src/libs/slide-event-emitter';
import SlideEventEmitter, { SLIDE_EVENTS } from 'js/src/libs/slide-event-emitter';
import uuid from 'js/src/libs/uuid';
import WebSocketClient from 'js/src/libs/websocket-client';
import {
    cutVoicedrivenSceneGroupOperation,
    saveRawLuminary,
    updateSceneGroupDurationToEqualVoiceDrivenAssetDurationOperation,
} from 'js/src/luminary';
import type { TimingSpecification } from 'js/src/luminary/asset-timing-operations';
import { setAssetIntroExitTimesOperation } from 'js/src/luminary/asset-timing-operations';
import { equalWithinEpsilon } from 'js/src/luminary/comparison-utils';
import { createLuminaryOperation } from 'js/src/luminary/luminary-hooks';
import {
    getVoiceDrivenAssetPlaybackRegions,
    getVoiceDrivenAssetSourceData,
    getVoiceDrivenBaseAssetCutRegions,
    getVoiceDrivenBaseAssetEndTime,
    getVoiceDrivenBaseAssetSourceDuration,
    getVoiceDrivenBaseAssetStartTime,
    isAIVoiceoverSceneGroup,
    translateTranscriptTimeToVideoTime,
    translateVideoTimeToSceneGroupTime,
    translateVideoTimeToTranscriptTime,
} from 'js/src/luminary/luminary-selectors';
import {
    setVoiceDrivenBaseAssetEndTime,
    setVoiceDrivenBaseAssetStartTime,
} from 'js/src/luminary/luminary-utils';
import {
    getVoicedrivenSceneEndTime,
    getVoicedrivenSceneStartTime,
    runVoicedrivenTranscriptBatchOperation,
    runVoicedrivenTranscriptBatchWithPrecapturedInterestPointsOperation,
    setVoicedrivenSceneDuration,
} from 'js/src/luminary/voicedriven-scenes';
import { isErrorObject } from 'js/src/types';

// Gap (ms) to preserve between a filler cut boundary and an adjacent word.
// Set to 0 to consume the full inter-word gap. Luminary's 10ms gain ramp
// (QUICK_TRANSITION_DURATION) at playback region boundaries already provides
// a smooth crossfade that prevents hard clipping of adjacent speech.
const FILLER_ADJACENT_WORD_GUARD_MS = 0;

export function convertTranscriptWordsToMillis<T extends EditedTranscriptWord | TranscriptWord>(
    transcriptWords: T[],
    audioDurationMillis?: number,
): T[] {
    return transcriptWords.map((word) => {
        let startTime = word.startTime * 1000;
        let endTime = word.endTime * 1000;

        // Ensure non-negative duration first (handle zero-duration edge case from word_segmentation.py)
        if (endTime <= startTime) {
            endTime = startTime + 1;
        }

        // Then clamp to audio duration if provided (fixes LUM-77)
        if (audioDurationMillis !== undefined) {
            startTime = Math.min(startTime, audioDurationMillis);
            endTime = Math.min(endTime, audioDurationMillis);
        }

        return { ...word, startTime, endTime };
    });
}

export const setWordLevelPronunciation = (
    editor: LexicalEditor,
    wordIds: string[],
    pronunciation: string,
) => {
    const trimmedPronunciation = pronunciation.trim();
    const selectedWordIds = new Set(wordIds);
    return updateEditor(editor, () => {
        // Words carrying an override share a `pronunciationId` and are spoken as one
        // phrase, so an override can only be rewritten as a whole: leaving part of a group
        // behind would have it speak the old phonetic a second time. Collect the groups the
        // selection touches, then write to every word of them.
        const groupIds = new Set<string>();
        let remainingSelectedWords = selectedWordIds.size;
        for (const { node } of $dfsIterator()) {
            if (remainingSelectedWords === 0) {
                break;
            }
            if ($isTranscriptWordNode(node) && selectedWordIds.has(node.id)) {
                if (node.pronunciationId) {
                    groupIds.add(node.pronunciationId);
                }
                remainingSelectedWords -= 1;
            }
        }

        const pronunciationId = trimmedPronunciation ? uuid() : undefined;
        const touchesNoGroup = groupIds.size === 0;
        remainingSelectedWords = selectedWordIds.size;
        for (const { node } of $dfsIterator()) {
            if (touchesNoGroup && remainingSelectedWords === 0) {
                break;
            }
            if (!$isTranscriptWordNode(node)) {
                continue;
            }
            const isSelected = selectedWordIds.has(node.id);
            const belongsToTouchedGroup =
                node.pronunciationId !== undefined && groupIds.has(node.pronunciationId);
            if (isSelected || belongsToTouchedGroup) {
                node.pronunciation = trimmedPronunciation || undefined;
                node.pronunciationId = pronunciationId;
                if (isSelected) {
                    remainingSelectedWords -= 1;
                }
            }
        }
    });
};

/**
 * Remove the word-level pronunciation override from the given words, and from the rest of
 * any override group they belong to.
 */
export const clearWordLevelPronunciations = (editor: LexicalEditor, wordIds: string[]) =>
    setWordLevelPronunciation(editor, wordIds, '');

export function hydrateTranscripts(
    transcripts: Record<string, SerializedTranscript | SerializedEditorState>,
) {
    return (dispatch: Dispatch) => {
        const serializedTranscripts: Record<string, SerializedTranscript> = {};
        for (const sceneGroupId in transcripts) {
            let editorState: SerializedEditorState = (
                transcripts[sceneGroupId] as SerializedTranscript
            ).editorState;
            if (editorState) {
                const transcript = transcripts[sceneGroupId] as SerializedTranscript;
                const disableOverlays = transcript.disableOverlays === true;
                const translations = ImmutableFromJS(transcript.translations);
                const language = transcript.language;
                dispatch(
                    storeRawTranscript({
                        sceneGroupId,
                        editorState,
                        isOriginal: false,
                        language,
                        systemTriggered: true,
                        parentActionGroup: undefined,
                    }),
                );
                disableOverlays &&
                    dispatch(
                        disableTranscriptOverlays({
                            sceneGroupId,
                            systemTriggered: true,
                            parentActionGroup: undefined,
                        }),
                    );

                if (translations) {
                    dispatch(
                        storeTranscriptTranslations({
                            sceneGroupId,
                            translations,
                            systemTriggered: true,
                            parentActionGroup: undefined,
                        }),
                    );
                }

                serializedTranscripts[sceneGroupId] = transcripts[
                    sceneGroupId
                ] as SerializedTranscript;
            } else {
                editorState = transcripts[sceneGroupId] as SerializedEditorState;
                dispatch(
                    storeRawTranscript({
                        sceneGroupId,
                        editorState,
                        isOriginal: false,
                        systemTriggered: true,
                        parentActionGroup: undefined,
                    }),
                );
            }
        }

        // store translations, work with only SerializedTranscript types because we need transcript[sceneGroupId].translations
        if (Object.keys(serializedTranscripts).length > 0) {
            const translationEditors =
                reduceTranscriptsToTranslationEditorsMap(serializedTranscripts);
            translationEditors.forEach((translationEditor) =>
                dispatch(storeTranslatedEditors(translationEditor)),
            );
        }
    };
}

export function hydrateLexicalEditorsFromRedux() {
    return (dispatch: Dispatch, getState: GetRootState) => {
        const state = getState();
        const transcriptDataOrm = new StoreORM<TranscriptData, string>(getTranscripts(state));
        const editors: LexicalEditorsDataType[] = [];
        transcriptDataOrm.forEach((transcriptData) => {
            editors.push({
                editor: createEditorFromEditorState(transcriptData.get('editorState')),
                sceneGroupId: transcriptData.get('sceneGroupId'),
                lexicalControlsItsOwnState: false,
            });
        });
        dispatch(overwriteAllLexicalEditors(editors));
    };
}

export function createEditorFromEditorState(
    editorState: SerializedEditorState | ImmutableMap<SerializedEditorState>,
) {
    const editor = createLexicalEditor(TRANSCRIPT_EDITOR_CONFIG);
    const parsedEditorState = editor.parseEditorState(JSON.stringify(editorState));
    // Editor state doesn't get set if it's not in headless mode.
    // This isn't ideal but it's at least an ok temporary solution.
    editor._headless = true;
    editor.setEditorState(parsedEditorState);
    editor._headless = false;
    return editor;
}

type CheckpointVideoAndTranscriptOptions = {
    isOriginalTranscript?: boolean;
    waitForLuminary?: boolean;
};

export const checkpointVideoAndAllTranscriptsOperation = createLuminaryOperation(
    (
        { luminary, dispatch, getState, call },
        options: CheckpointVideoAndTranscriptOptions = {
            isOriginalTranscript: false,
            waitForLuminary: false,
        },
        systemTriggered: boolean = false,
        parentActionGroup: string = uuid(),
    ) => {
        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );
        const orm = new StoreORM<LexicalEditorsDataType, string>(getLexicalEditors(getState()));
        orm.forEach((editorData) => {
            call(
                saveTranscriptOperation,
                editorData.get('sceneGroupId'),
                editorData.get('editor') as unknown as LexicalEditor, // ImmutableJS confuses typescript
                {
                    isOriginal: options.isOriginalTranscript,
                    waitForLuminary: options.waitForLuminary,
                },
                systemTriggered,
                parentActionGroup,
            );
        });
    },
);

export const checkpointVideoAndTranscriptOperation = createLuminaryOperation(
    (
        { luminary, dispatch, getState, call },
        sceneGroupId: string,
        options: CheckpointVideoAndTranscriptOptions = {
            isOriginalTranscript: false,
            waitForLuminary: false,
        },
        systemTriggered: boolean = false,
        parentActionGroup: string = uuid(),
    ) => {
        const editor = getEditorFromSceneGroupId(getState(), sceneGroupId);
        if (!editor) {
            return;
        }
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) {
            return;
        }
        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );
        call(
            saveTranscriptOperation,
            sceneGroupId,
            editor,
            { isOriginal: options.isOriginalTranscript, waitForLuminary: options.waitForLuminary },
            systemTriggered,
            parentActionGroup,
        );
    },
);

type SaveTranscriptOptions = {
    isOriginal?: boolean;
    language?: LanguageEnum;
    waitForLuminary?: boolean;
};
export const saveTranscriptOperation = createLuminaryOperation(
    (
        { luminary, dispatch, getState },
        sceneGroupId: string,
        editor: LexicalEditor,
        options: SaveTranscriptOptions = {
            isOriginal: false,
            language: undefined,
            waitForLuminary: true,
        },
        systemTriggered: boolean = false,
        parentActionGroup: string = uuid(),
    ) => {
        /*
        editor.dispatchCommand dispatches a command, allowing potentially multiple listeners to
        react to the command using editor.update(callback). At the end of the editor.update()
        function, lexical enqueues a microtask to actually commit the updates:
        https://github.com/facebook/lexical/blob/5bfb7300e23614a8ca1ddd747352e8e057143cc2/packages/lexical/src/LexicalUpdates.ts#L953-L955

        We want to make sure that the transcript that we are saving to redux is the most up-to-date one (after
        applying any potential changes from the command listeners).

        So we also need to use queueMicrotask here!
        */
        queueMicrotask(async () => {
            /*
            The idea with the transcript validation plugin is that we can:

            - make changes to luminary
            - make changes to the editor using `useTranscriptOperation`
            - validate + save the transcript

            As long as that order is followed, and the changes are synchronous, everything works well.
            However, sometimes our code needs to load something in luminary before the changes can be finalized.

            For example, we might:

            - make changes to luminary
            - make changes to the editor using `useTranscriptOperation`
            - await luminary.onCanPlay() -> read some luminary state, then make more changes to luminary
            - validate + save the transcript

            In this case, we also need the editor validation + saving code to wait for luminary to complete it's update.
            That's why we put `await luminary.onCanPlay()` here.
            */
            if (options.waitForLuminary) {
                await luminary.onCanPlay();
            }

            editor.dispatchCommand(VALIDATE_TRANSCRIPT_COMMAND, undefined);
            const editorState = editor.getEditorState();
            dispatch(
                saveTranscript(
                    sceneGroupId,
                    editorState,
                    !!options.isOriginal,
                    options.language,
                    systemTriggered,
                    parentActionGroup,
                ),
            );
        });
    },
);

export function saveTranscript(
    sceneGroupId: string,
    editorState: EditorState,
    isOriginal: boolean,
    language?: LanguageEnum,
    systemTriggered: boolean = false,
    parentActionGroup: string = uuid(),
) {
    return (dispatch: AppDispatch) => {
        if (!editorState.isEmpty()) {
            dispatch(
                storeRawTranscript({
                    sceneGroupId,
                    editorState: editorState.toJSON(),
                    isOriginal,
                    language,
                    systemTriggered,
                    parentActionGroup,
                }),
            );
        }
    };
}

export {
    $generateEditorFromTranscript,
    SilenceNodeGenerationMethod,
} from 'js/src/data/transcript/generate-editor-from-transcript';

export const buildTranslatedTranscript = (
    translatedWordNodes: TranscriptWord[],
    langCode: LanguageEnum,
) => {
    return async (dispatch: AppDispatch, getState: GetRootState) => {
        // Create a temporary editor instance using a temporary hidden dom element
        const editor = createLexicalEditor(
            TRANSCRIPT_EDITOR_CONFIG,
            getNextTranslationEditorName(),
        );
        const contentEditableElement = document.createElement('div');
        contentEditableElement.style.display = 'none';
        editor.setRootElement(contentEditableElement);

        const useSpaces = shouldSeparateWordsWithSpaces(getState(), langCode || LanguageEnum.XX);
        await updateEditor(editor, () => {
            $generateEditorFromTranscript(
                translatedWordNodes,
                useSpaces,
                SilenceNodeGenerationMethod.DoNotGenerate,
            );
        });

        return editor;
    };
};

/**
 * This function creates a temporary editor instance using a temporary hidden DOM element.
 * The main outcome is so that we can create and store the initial editor, which will be used throughout
 * the rest of the application.
 */
export const loadNewTranscriptOperation = createLuminaryOperation(
    (
        { luminary, dispatch, getState, call },
        transcriptWords: TranscriptWord[],
        transcriptLanguage: LanguageEnum | undefined,
        sceneGroupId: string,
        sceneId: string,
        showTranscript: boolean,
        isOnboarding: boolean = false,
        creationStartTime: number | null = null,
        isFromScene: boolean = false,
        systemTriggered: boolean = false,
        parentActionGroup: string = uuid(),
    ) => {
        // Create a temporary editor instance using a temporary hidden dom element
        const contentEditableElement = document.createElement('div');
        const editor = createLexicalEditor(TRANSCRIPT_EDITOR_CONFIG);
        contentEditableElement.style.display = 'none';
        editor.setRootElement(contentEditableElement);

        return new Promise<LexicalEditor>((resolve) => {
            updateEditor(editor, () => {
                const useSpaces = shouldSeparateWordsWithSpaces(
                    getState(),
                    transcriptLanguage || LanguageEnum.XX,
                );
                const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
                if (!sceneGroup) {
                    return;
                }
                const durationMillis = getVoiceDrivenBaseAssetSourceDuration(sceneGroup);
                const silenceNodeGenerationMethod = isAIVoiceoverSceneGroup(sceneGroup)
                    ? SilenceNodeGenerationMethod.GenerateFromTranscriptWord
                    : SilenceNodeGenerationMethod.GenerateFromWordGap;
                $generateEditorFromTranscript(
                    transcriptWords,
                    useSpaces,
                    silenceNodeGenerationMethod,
                    durationMillis,
                );
            }).then(() => {
                dispatch(
                    saveTranscript(
                        sceneGroupId,
                        editor.getEditorState(),
                        true,
                        transcriptLanguage,
                        systemTriggered,
                        parentActionGroup,
                    ),
                );
                dispatch(
                    storeLexicalEditor({ lexicalControlsItsOwnState: false, sceneGroupId, editor }),
                );
                // Disable overlays for transcripts that are created from scene captions (MR-2572)
                if (isFromScene) {
                    dispatch(
                        disableTranscriptOverlays({
                            sceneGroupId,
                            systemTriggered: true,
                            parentActionGroup: undefined,
                        }),
                    );
                    dispatch(setSceneConversionModalVisible(true));
                }
                showTranscript && dispatch(setTranscriptTabVisibility(true));
                // We have saved the editor state, so we can safely delete the dom element
                contentEditableElement && contentEditableElement.remove();

                window.analytics?.track('Transcript initialized', {
                    sceneGroupId,
                    sceneId,
                    numWords: transcriptWords.length,
                    transcriptDuration:
                        transcriptWords.length > 1
                            ? transcriptWords.at(-1)!.endTime - transcriptWords[0].startTime
                            : 0,
                    isOnboarding,
                    creationTime: creationStartTime ? performance.now() - creationStartTime : null,
                });
                resolve(editor);
            });
        });
    },
);

export function duplicateTranscript(
    sceneGroupId: string,
    sourceEditorState: SerializedEditorState,
    language: LanguageEnum | undefined,
    newSceneIdMap: { [sceneId: string]: string },
    systemTriggered: boolean,
    parentActionGroup: string,
) {
    return (dispatch: AppDispatch) => {
        const editor = createHeadlessEditor(TRANSCRIPT_EDITOR_CONFIG);
        const editorState = editor.parseEditorState(JSON.stringify(sourceEditorState));
        editor.setEditorState(editorState);
        updateEditor(
            editor,
            () => {
                const nodes = $nodesOfType(OverlayElementNode);
                if (nodes.length > 0) {
                    for (const node of nodes) {
                        node.setSceneId(newSceneIdMap[node.sceneId]);
                        node.setState(OverlayState.Default);
                    }
                } else {
                    // onUpdate (below) is only called if an update has been made; in this case, if there is an overlay
                    // so we also need to call saveTranscript here too to handle the scenario where there are no overlays
                    dispatch(
                        saveTranscript(
                            sceneGroupId,
                            editor.getEditorState(),
                            false,
                            language,
                            systemTriggered,
                            parentActionGroup,
                        ),
                    );
                }
            },
            {
                onUpdate: () => {
                    dispatch(
                        saveTranscript(
                            sceneGroupId,
                            editor.getEditorState(),
                            false,
                            language,
                            systemTriggered,
                            parentActionGroup,
                        ),
                    );
                },
            },
        );
    };
}

export function storeTranscriptTranslationsFromEditor(
    editor: LexicalEditor,
    translatedLanguage: string,
    sceneGroupId: string,
    systemTriggered: boolean,
    parentActionGroup: string,
) {
    return (dispatch: AppDispatch) => {
        const editorState = editor.getEditorState();
        if (editorState.isEmpty()) {
            return;
        }

        const translations = {
            [translatedLanguage]: { editorState: editorState.toJSON() },
        } as TranscriptTranslations;
        dispatch(
            storeTranscriptTranslations({
                sceneGroupId,
                translations: ImmutableFromJS(translations),
                systemTriggered,
                parentActionGroup,
            }),
        );
    };
}

/**
 * This updates the current time of the transcript by performing two actions:
 * 1. Updates the redux state for the current time relative to the scene group
 * 2. Emits a slide event to trigger a new preview to be generated and used
 */
export function updateTranscriptCurrentTimeAndPreview(payload: {
    sceneGroupId: string;
    sceneId: string;
    renderPreviewAtTime: number;
    playPreviewFromTime?: number;
}) {
    return (dispatch: AppDispatch) => {
        const transcriptCurrentTime = payload.playPreviewFromTime ?? payload.renderPreviewAtTime;
        dispatch(
            setTranscriptCurrentTime({
                sceneGroupId: payload.sceneGroupId,
                sceneId: payload.sceneId,
                currentTimeMillis: transcriptCurrentTime,
            }),
        );
        SlideEventEmitter.emit(SLIDE_EVENTS.UPDATE_PREVIEW_AT_TIME, {
            sceneGroupId: payload.sceneGroupId,
            timeRelativeToSceneGroup: payload.renderPreviewAtTime,
        });
    };
}

/**
 * Renders a preview of a single scene at a time measured from that scene's own start, rather
 * than from the start of its scene group.
 */
export const previewSceneAtSceneTimeOperation = createLuminaryOperation(
    (
        { luminary, dispatch },
        sceneGroupId: string,
        sceneId: string,
        timeRelativeToScene: number,
    ) => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        const scene = luminary.getSceneById(sceneId);
        if (!sceneGroup || !scene) {
            return;
        }
        dispatch(
            updateTranscriptCurrentTimeAndPreview({
                sceneGroupId,
                sceneId,
                renderPreviewAtTime: translateVideoTimeToSceneGroupTime(
                    scene.startTime + timeRelativeToScene,
                    sceneGroup.startTime,
                ),
            }),
        );
    },
);

/**
 * This function is useful to find what the expected boundary of an overlay should be (when it should start and end).
 * It takes into account the state of luminary (if there are other overlays that are immediately neighbouring "this" one),
 * as well as the state of the transcript (the word node boundaries).
 * @param startNode the first node in "this" overlay
 * @param endNode the last node in "this overlay"
 * @param sceneGroupId the sceneGroupId for the transcript
 * @returns {{startTime: number, endTime: number}} the proper start and end time of the overlay, in video time
 */
export const getOverlayTimeBoundaryFromLuminaryOperation = createLuminaryOperation(
    (
        { luminary },
        startNode: TranscriptWordNode,
        endNode: TranscriptWordNode,
        sceneGroupId: string,
    ) => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) {
            throw new Error('Scene group missing from luminary!');
        }
        const voiceDrivenAssetPlaybackRegions = getVoiceDrivenAssetPlaybackRegions(sceneGroup);
        const voiceDrivenAssetSourceDuration = getVoiceDrivenBaseAssetSourceDuration(sceneGroup);
        let { startTime, endTime } = $getNodeTimeBounds(
            startNode,
            endNode,
            voiceDrivenAssetPlaybackRegions,
        );
        startTime = translateTranscriptTimeToVideoTime(
            startTime,
            voiceDrivenAssetPlaybackRegions,
            voiceDrivenAssetSourceDuration,
            sceneGroup.startTime,
        );
        endTime = translateTranscriptTimeToVideoTime(
            endTime,
            voiceDrivenAssetPlaybackRegions,
            voiceDrivenAssetSourceDuration,
            sceneGroup.startTime,
        );

        const startNodeOverlaySceneId = $getNearestNodeOfType(
            startNode,
            OverlayElementNode,
        )?.sceneId;
        const endNodeOverlaySceneId = $getNearestNodeOfType(endNode, OverlayElementNode)?.sceneId;

        const overlaySceneIdRightBefore = $getOverlaySceneIdImmediatelyBefore(startNode);
        const overlaySceneIdRightAfter = $getOverlaySceneIdImmediatelyAfter(endNode);

        if (
            (overlaySceneIdRightBefore || startNodeOverlaySceneId) &&
            overlaySceneIdRightBefore !== startNodeOverlaySceneId
        ) {
            const usingOverlaySceneBefore = !!overlaySceneIdRightBefore;
            const sceneId = usingOverlaySceneBefore
                ? overlaySceneIdRightBefore
                : startNodeOverlaySceneId;
            if (!sceneId) {
                throw new Error('Overlay scene ID not found');
            }
            const overlay = luminary.getSceneById(sceneId);
            if (!overlay) {
                throw new Error('Overlay missing from luminary!');
            }
            startTime = usingOverlaySceneBefore
                ? getVoicedrivenSceneEndTime(overlay)
                : getVoicedrivenSceneStartTime(overlay);
        }
        if (
            (overlaySceneIdRightAfter || endNodeOverlaySceneId) &&
            overlaySceneIdRightAfter !== endNodeOverlaySceneId
        ) {
            const usingOverlaySceneAfter = !!overlaySceneIdRightAfter;
            const sceneId = usingOverlaySceneAfter
                ? overlaySceneIdRightAfter
                : endNodeOverlaySceneId;
            if (!sceneId) {
                throw new Error('Overlay scene ID not found');
            }
            const overlay = luminary.getSceneById(sceneId);
            if (!overlay) {
                throw new Error('Overlay missing from luminary!');
            }
            endTime = usingOverlaySceneAfter
                ? getVoicedrivenSceneStartTime(overlay)
                : getVoicedrivenSceneEndTime(overlay);
        }

        return { startTime, endTime };
    },
);

/**
 * Often, we have a range of TranscriptWordNodes in the transcript, and we want to convert that
 * into the start/end time represented by that range of nodes. Eg. the user selects a couple words
 * and wants to cut that part of the video out.
 *
 * This is actually more complicated than it might seem - we want to watch out for overlay boundaries,
 * and cut boundaries, to line up with those.
 *
 * @returns { startTime, endTime } - the start and end time represented by the startNode and endNode (in transcript time)
 */
export const $getTimeIndicatedBy = createLuminaryOperation(
    (
        { luminary },
        startNode: TranscriptWordNode,
        endNode: TranscriptWordNode,
        sceneGroupId: string,
    ) => {
        const wordBeforeFirstSelectedWord = $getPreviousTranscriptWordNode(startNode, {
            includeSpaces: false,
            includeTrimmedWords: false,
        });
        const wordAfterLastSelectedWord = $getNextTranscriptWordNode(endNode, {
            includeSpaces: false,
            includeTrimmedWords: false,
        });

        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) {
            throw new Error('Scene group missing from luminary!');
        }
        const voiceDrivenAssetPlaybackRegions = getVoiceDrivenAssetPlaybackRegions(sceneGroup);
        const voiceDrivenAssetSourceDuration = getVoiceDrivenBaseAssetSourceDuration(sceneGroup);
        const preExistingCuts = getVoiceDrivenBaseAssetCutRegions(sceneGroup);

        const sceneStartTimes = sceneGroup.scenes.map((scene) => {
            return translateVideoTimeToTranscriptTime(
                getVoicedrivenSceneStartTime(scene),
                voiceDrivenAssetPlaybackRegions,
                voiceDrivenAssetSourceDuration,
                sceneGroup.startTime,
            );
        });

        let startTime = 0;
        if (wordBeforeFirstSelectedWord) {
            // if there is a scene start time between the first word selected and the word just before the
            // selection, then the selection reflects the scene start time
            const sceneStartTimeBetween = sceneStartTimes.find((sceneStartTime) => {
                return thisIsWithinRange(sceneStartTime, [
                    wordBeforeFirstSelectedWord.endTime,
                    startNode.startTime,
                ]);
            });
            // similar for pre-existing cuts
            const preExistingCutBetween = preExistingCuts.find(([startTime, endTime]) => {
                return theseAreOverlapping(
                    wordBeforeFirstSelectedWord.endTime,
                    startNode.startTime,
                    startTime,
                    endTime,
                );
            });
            if (sceneStartTimeBetween) {
                startTime = sceneStartTimeBetween;
            } else if (preExistingCutBetween) {
                startTime = preExistingCutBetween[1];
            } else {
                // otherwise, the selection reflects the midpoint between the two words
                startTime = wordGapMidpoint(wordBeforeFirstSelectedWord, startNode);
            }
        }

        // same for endTime
        let endTime = voiceDrivenAssetSourceDuration;
        if (wordAfterLastSelectedWord) {
            const sceneStartTimeBetween = sceneStartTimes.find((sceneStartTime) => {
                return thisIsWithinRange(sceneStartTime, [
                    endNode.endTime,
                    wordAfterLastSelectedWord.startTime,
                ]);
            });
            const preExistingCutBetween = preExistingCuts.find(([startTime, endTime]) => {
                return theseAreOverlapping(
                    endNode.endTime,
                    wordAfterLastSelectedWord.startTime,
                    startTime,
                    endTime,
                );
            });
            if (sceneStartTimeBetween) {
                endTime = sceneStartTimeBetween;
            } else if (preExistingCutBetween) {
                endTime = preExistingCutBetween[0];
            } else {
                endTime = wordGapMidpoint(endNode, wordAfterLastSelectedWord);
            }
        }

        return { startTime, endTime };
    },
);

export const $getTimeIndicatedBySelection = createLuminaryOperation(
    ({ call }, sceneGroupId: string) => {
        const selectedNodes = $getSelectedNodes();
        const selectedWords = selectedNodes.filter($isTranscriptWordNode);
        const firstWord = selectedWords[0];
        const lastWord = selectedWords[selectedWords.length - 1];
        return call($getTimeIndicatedBy, firstWord, lastWord, sceneGroupId);
    },
);

/**
 * This operation will play the scene.
 */
export function playScene(payload: StartScenePreviewPlaybackPayload) {
    return () => {
        SlideEventEmitter.emit(SLIDE_EVENTS.START_SCENE_PREVIEW_PLAYBACK, payload);
    };
}

export const syncTranscriptToLuminaryOperation = createLuminaryOperation(
    (
        { luminary, getState },
        editor: LexicalEditor,
        sceneGroupId: string,
        typeOfTranscript: TypeOfTranscript,
    ) => {
        const state = getState();
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) {
            return;
        }

        const words = getTranscriptWords(editor) as TranscriptWordData[];

        switch (typeOfTranscript) {
            // update the original transcript
            case TypeOfTranscript.MEDIA_TRANSCRIPT_TAB:
            case TypeOfTranscript.CLIP_CREATOR_MAIN:
            case TypeOfTranscript.CLIP_CREATOR_CLIP: {
                const transcript = getTranscriptById(state, sceneGroupId);
                if (!transcript) {
                    return;
                }
                const language = transcript.get('language') ?? LanguageEnum.XX;
                const separateWordsWithSpaces = shouldSeparateWordsWithSpaces(state, language);
                const textDirection = getLanguageTextDirection(state, language);

                const transcriptData: LuminaryTranscriptData = {
                    language,
                    textDirection,
                    words,
                    separateWordsWithSpaces,
                };
                sceneGroup.originalTranscript.transcriptData = transcriptData;

                // when our transcript is not translated, then we also use it for our captions
                if (!getTranscriptTranslationLanguage(state, sceneGroupId)) {
                    sceneGroup.captions.transcript.transcriptData = transcriptData;
                }
                break;
            }
            // update the translated transcript
            case TypeOfTranscript.TRANSLATION: {
                const language = getTranscriptTranslationLanguage(state, sceneGroupId);
                if (!language) {
                    return;
                }
                const separateWordsWithSpaces = shouldSeparateWordsWithSpaces(state, language);
                const textDirection = getLanguageTextDirection(state, language);

                sceneGroup.captions.transcript.transcriptData = {
                    language,
                    textDirection,
                    words,
                    separateWordsWithSpaces,
                };
                break;
            }
            default: {
                throw new Error('Unknown type of transcript');
            }
        }
    },
);

// Todo: This is temporarily here instead of 'transcript-types' to avoid strict null check errors. This should be generated from the backend
export type EditVoiceoverTaskResponse = {
    transcript: EditedTranscriptWord[];
    audio_asset: AudioAssetModel;
    // Per-scene end times in seconds, returned during AI voiceover translation.
    // Computed on the backend by aligning ElevenLabs TTS word timings to per-scene
    // caption splits via TranscriptAligner. The frontend uses these to set overlay
    // scene durations so scene breaks align with word boundaries.
    scene_boundary_times?: number[];
};

/**
 * The lexical editor a voiceover edit of this type reads from and writes back to.
 */
const getEditorForTypeOfTranscript = (
    state: ReturnType<GetRootState>,
    sceneGroupId: string,
    typeOfTranscript: TypeOfTranscript,
) =>
    typeOfTranscript === TypeOfTranscript.MEDIA_TRANSCRIPT_TAB
        ? getEditorFromSceneGroupId(state, sceneGroupId)
        : getTranslatedEditorBySceneGroupId(state, sceneGroupId);

export const editAIVoiceoverOperation = createLuminaryOperation(
    async (
        { luminary, dispatch, getState, call },
        {
            sceneGroupId,
            transcript,
            langCode,
            originalVoiceoverAssetSourceData,
            newVoice,
            newVoiceSpeed,
            typeOfTranscript = TypeOfTranscript.MEDIA_TRANSCRIPT_TAB,
        }: {
            sceneGroupId: string;
            transcript: EditedTranscriptWordRequest[];
            langCode: LanguageEnum;
            originalVoiceoverAssetSourceData: AudioAssetSourceData;
            newVoice?: string;
            newVoiceSpeed?: number | null;
            typeOfTranscript?: TypeOfTranscript;
        },
        systemTriggered: boolean = false,
        parentActionGroup: string = uuid(),
    ) => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) {
            return;
        }

        // `transcript` was sampled from this editor by the caller. Reconciling the regenerated
        // voiceover only works against that same editor: the backend echoes back the word ids it
        // was sent, and the reconciliation matches them to scene boundaries by id.
        const editorTranscriptWasSampledFrom = getEditorForTypeOfTranscript(
            getState(),
            sceneGroupId,
            typeOfTranscript,
        );

        dispatch(
            markSceneGroupAsLoading({
                sceneGroupId,
                type: SCENE_GROUP_LOADING_TYPES.RECOMMENDING_OVERLAYS,
                context: { loadingMsg: LOADING_SCENE_MSG.AI_VOICEOVER_EDIT },
            }),
        );
        try {
            const ws = WebSocketClient.getInstance();
            const response = await ApiService.editVoiceoverUpdate({
                body: {
                    full_transcript: transcript,
                    language: langCode,
                    original_audio_asset_source_data: originalVoiceoverAssetSourceData,
                    trim_end_millis: getVoiceDrivenBaseAssetEndTime(sceneGroup),
                    trim_start_millis: getVoiceDrivenBaseAssetStartTime(sceneGroup),
                    context_select_full_transcript:
                        typeOfTranscript === TypeOfTranscript.TRANSLATION,
                    new_voice: newVoice,
                    new_voice_speed: newVoiceSpeed,
                    video_id: getVideoId(getState()),
                },
            });
            const result = await ws.waitForMessage<EditVoiceoverTaskResponse>(
                response.data.async_operation_uuid,
                AsyncOperationMessageTypeEnum.ASYNC_OPERATION_RESULT,
                AsyncOperationNameEnum.GENERATE_EDITED_VOICEOVER,
            );

            // An undo, a redo or a checkpoint reset landing while the backend was working rebuilds
            // every lexical editor from its serialized state (`hydrateLexicalEditorsFromRedux`), and
            // word ids do not survive that round trip — `TranscriptWordNode.importJSON` mints a new
            // one for every node. The editor now holds words the backend never saw, so every scene
            // boundary lookup in the reconciliation would miss and the edit would be rejected part
            // way through. The transcript this result was computed from is gone anyway, so drop the
            // result rather than re-timing the scenes against a transcript the user moved away from.
            if (
                getEditorForTypeOfTranscript(getState(), sceneGroupId, typeOfTranscript) !==
                editorTranscriptWasSampledFrom
            ) {
                dispatch(markSceneGroupAsFinishedLoading({ sceneGroupId }));
                return;
            }

            // While transcript and luminary are updated, we don't want the LuminarySyncPlugin to run
            // So set lexical to controlling its own state until its done
            dispatch(startLexicalControllingItsOwnState({ sceneGroupId }));

            await call(
                recreateEditorAndMaintainOverlayToWordRelationshipsOperation,
                result,
                sceneGroupId,
                typeOfTranscript,
                systemTriggered,
                parentActionGroup,
            );
            // Now that all scene durations are updated, update the scene group duration
            await call(updateSceneGroupDurationToEqualVoiceDrivenAssetDurationOperation, {
                sceneGroup,
            });
        } catch (e) {
            // Make sure to throw any error so that the caller can handle and undo any changes
            dispatch(endLexicalControllingItsOwnState({ sceneGroupId }));
            dispatch(markSceneGroupAsFinishedLoading({ sceneGroupId }));
            call(resetLuminaryAndLexicalToPresentCheckpointOperation);
            throw e;
        }
        // All updates are done, let lexical control its own state again and allow LuminarySuncPlugin
        // to sync changes
        dispatch(endLexicalControllingItsOwnState({ sceneGroupId }));
        dispatch(markSceneGroupAsFinishedLoading({ sceneGroupId }));

        // GenX only: the new voice may have lengthened scenes, leaving clips with
        // a stale playbackRate that loops. Recompute overrides so clips fill the
        // new durations without regenerating. No-op for non-GenX videos.
        await call(syncGenxClipPlaybackRatesOperation, sceneGroupId);

        // Checkpoint transcript and video data
        call(
            checkpointVideoAndTranscriptOperation,
            sceneGroupId,
            { waitForLuminary: true },
            systemTriggered,
            parentActionGroup,
        );

        // Handle tracking
        const regeneratedCharLength = transcript.reduce((len, word) => {
            if (word.contextSelected || word.userSelected) {
                return len;
            }
            return len + word.word.length;
        }, 0);

        BatchAnalytics.track(
            'Elevenlabs Character Usage',
            {},
            { num_characters: regeneratedCharLength },
        );
    },
);

type RegeneratedAudioSourceData = AudioAssetModel & { duration: number };

type RegeneratedVoiceoverPlan = {
    audioAssetSourceData: RegeneratedAudioSourceData;
    sceneDurations: { scene: Scene; duration: number }[];
    newStartTrimTime: number | undefined;
    newEndTrimTime: number | undefined;
};

const CANNOT_APPLY_REGENERATED_VOICEOVER = 'Cannot apply the regenerated voiceover';

/**
 * Works out everything a regenerated voiceover needs to write — the new audio, every scene's
 * duration and the trim points — and throws if any of it can't be derived from the regenerated
 * transcript, or would be rejected by luminary.
 *
 * Planning happens before anything is mutated so that a regeneration we can't fully apply leaves
 * the scene group exactly as it was. Otherwise a failure part way through re-timing the scenes
 * strands the scene group with the new audio and new word times over scenes that only some of the
 * new boundaries were applied to.
 */
export const planRegeneratedVoiceoverReconciliation = ({
    sceneGroup,
    newTranscriptWords,
    audioAssetSourceData,
    audioAssetDurationMillis,
    firstAndLastWordIdsBySceneId,
    baseAssetStartTime,
    hasEndTrim,
    leftTrimmedOutWordId,
}: {
    sceneGroup: SceneGroup;
    newTranscriptWords: EditedTranscriptWord[];
    audioAssetSourceData: RegeneratedAudioSourceData;
    audioAssetDurationMillis: number;
    firstAndLastWordIdsBySceneId: FirstAndLastWordIdsBySceneId;
    baseAssetStartTime: number;
    hasEndTrim: boolean;
    leftTrimmedOutWordId: string | undefined;
}): RegeneratedVoiceoverPlan => {
    const newWordsById = new Map(newTranscriptWords.map((word) => [word.id, word]));
    const regeneratedWord = (wordId: string, description: string) => {
        const word = newWordsById.get(wordId);
        if (!word) {
            throw new Error(
                `${CANNOT_APPLY_REGENERATED_VOICEOVER}: the ${description} (${wordId}) is not in the regenerated transcript`,
            );
        }
        return word;
    };
    const sceneWordIds = (scene: Scene) => firstAndLastWordIdsBySceneId[scene.id];
    const isBoundaryAnchorWord = (word: EditedTranscriptWord) => !!word.word.trim();

    const sceneDurations: RegeneratedVoiceoverPlan['sceneDurations'] = [];
    let newStartTime = baseAssetStartTime;
    let newEndTime = 0;
    for (const scene of sceneGroup.scenes) {
        const wordIds = sceneWordIds(scene);
        const nextScene = scene.nextScene;
        const isLastSceneOfSceneGroup = !nextScene || nextScene.sceneGroup?.id !== sceneGroup.id;

        if (isLastSceneOfSceneGroup && (!hasEndTrim || !wordIds)) {
            // The last scene runs to the end of the audio. A word-less last scene (base/waveform
            // sliver) also lands here: it has no word to re-anchor an end trim to, so run to the
            // audio end rather than collapsing to a 1ms sliver — otherwise the clamped boundary
            // would become the new end trim and silently drop the kept trailing audio.
            newEndTime = audioAssetDurationMillis;
        } else if (isLastSceneOfSceneGroup) {
            // The scene group stops before the end of the audio, so its last scene ends on a word.
            // Push the boundary to the midpoint with whatever is spoken next, as that word is
            // trimmed off rather than skipped.
            const lastWordOfSceneGroup = regeneratedWord(
                wordIds.lastWordId,
                `last word of scene ${scene.id}`,
            );
            newEndTime = lastWordOfSceneGroup.endTime;
            const nextWord = newTranscriptWords.find(
                (word) => isBoundaryAnchorWord(word) && word.startTime >= newEndTime,
            );
            if (nextWord) {
                newEndTime = wordGapMidpoint(lastWordOfSceneGroup, nextWord);
            }
        } else if (!wordIds) {
            // An interior scene with no transcript words — a base/waveform sliver left fully covered
            // by overlays, or a range emptied by a trim. It has no narration to time against, so give
            // it the minimum duration and carry the boundary forward.
            newEndTime = newStartTime + VideoData.MIN_SCENE_DURATION;
        } else {
            const lastWordOfScene = regeneratedWord(
                wordIds.lastWordId,
                `last word of scene ${scene.id}`,
            );
            const nextWordIds = nextScene ? sceneWordIds(nextScene) : undefined;
            // A word-less next scene has no first word to gap against, so end this scene at its own
            // last word; the next (word-less) scene then takes its minimum slot after it.
            newEndTime = nextWordIds
                ? wordGapMidpoint(
                      lastWordOfScene,
                      regeneratedWord(
                          nextWordIds.firstWordId,
                          `first word of scene ${nextScene!.id}`,
                      ),
                  )
                : lastWordOfScene.endTime;
        }

        // A regeneration can leave a scene's boundary at or before its start: out-of-order
        // regenerated word times, a word-less neighbour, or a carried clamp from an earlier scene.
        // Clamp to the minimum and carry the boundary forward so the whole edit still applies rather
        // than being hard-rejected (which was the single most common edit_ai_voiceover failure).
        if (newEndTime - newStartTime < VideoData.MIN_SCENE_DURATION) {
            newEndTime = newStartTime + VideoData.MIN_SCENE_DURATION;
        }
        sceneDurations.push({ scene, duration: newEndTime - newStartTime });
        newStartTime = newEndTime;
    }

    const newStartTrimTime: number | undefined = (() => {
        if (!baseAssetStartTime) {
            return;
        }
        // The regeneration re-times the first kept word (it sits inside the re-synthesized region),
        // so the boundary between the last trimmed-out word and the first kept word moves.
        // Re-anchor the start trim to that recomputed word boundary.
        const trimmedOutWordIndex = leftTrimmedOutWordId
            ? newTranscriptWords.indexOf(
                  regeneratedWord(leftTrimmedOutWordId, 'last word trimmed off the start'),
              )
            : -1;
        const trimmedOutWordEndTime =
            trimmedOutWordIndex >= 0 ? newTranscriptWords[trimmedOutWordIndex].endTime : 0;
        const firstKeptWord =
            trimmedOutWordIndex >= 0
                ? newTranscriptWords.slice(trimmedOutWordIndex + 1).find(isBoundaryAnchorWord)
                : newTranscriptWords[0];
        if (!firstKeptWord) {
            throw new Error(
                `${CANNOT_APPLY_REGENERATED_VOICEOVER}: the regenerated transcript has no word after the start trim`,
            );
        }
        // midpoint between the last trimmed-out word and the first kept word
        return (firstKeptWord.startTime + trimmedOutWordEndTime) / 2;
    })();
    if (
        newStartTrimTime !== undefined &&
        (newStartTrimTime < 0 || newStartTrimTime >= audioAssetDurationMillis)
    ) {
        throw new Error(
            `${CANNOT_APPLY_REGENERATED_VOICEOVER}: the start trim would move to ${newStartTrimTime}ms of ${audioAssetDurationMillis}ms of audio`,
        );
    }

    const newEndTrimTime = hasEndTrim ? newEndTime : undefined;
    if (
        newEndTrimTime !== undefined &&
        newEndTrimTime <= (newStartTrimTime ?? baseAssetStartTime)
    ) {
        throw new Error(
            `${CANNOT_APPLY_REGENERATED_VOICEOVER}: the trim points would leave no audio to play`,
        );
    }

    return { audioAssetSourceData, sceneDurations, newStartTrimTime, newEndTrimTime };
};

/**
 * After calling the backend to regenerate the AIVO, we need to reconcile the new transcript with the overlays
 * and trim points in the user's video.
 *
 * This function assumes that the words in the new transcript are the same as the old transcript. It
 * leverages that assumption to make sure that overlay boundaries are placed between the same words that they
 * previously existed between.
 */
export const recreateEditorAndMaintainOverlayToWordRelationshipsOperation = createLuminaryOperation(
    async (
        { luminary, dispatch, getState, call },
        result: EditVoiceoverTaskResponse,
        sceneGroupId: string,
        typeOfTranscript: TypeOfTranscript,
        systemTriggered: boolean,
        parentActionGroup: string,
    ) => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup || !isAIVoiceoverSceneGroup(sceneGroup)) {
            return;
        }

        const editor = getEditorForTypeOfTranscript(getState(), sceneGroupId, typeOfTranscript);

        if (!editor) {
            return;
        }

        const transcriptResult = convertTranscriptWordsToMillis(result.transcript);

        // the backend rounds the duration to the nearest second. If the backend
        // coincidentally rounded the duration down, there could be words AFTER the
        // end of the audio asset, which should be impossible. Eg. if the audio is
        // actually 10.4 seconds long, there could be a word at 10.2 - 10.3 seconds.
        // That duration would be rounded down by the backend to 10 seconds.
        // To avoid words after the end of the audio, just take the max of the two:
        const audioAssetDurationMillis = Math.max(
            getTranscriptDuration(transcriptResult),
            result.audio_asset.duration * 1000,
        );
        const audioAssetSourceData = {
            ...result.audio_asset,
            duration: audioAssetDurationMillis / 1000.0,
        };

        const baseAssetStartTime = getVoiceDrivenBaseAssetStartTime(sceneGroup);
        const baseAssetEndTime = getVoiceDrivenBaseAssetEndTime(sceneGroup);
        const hasEndTrim = !equalWithinEpsilon(
            baseAssetEndTime,
            getVoiceDrivenBaseAssetSourceDuration(sceneGroup),
        );

        const scenes = sceneGroup.scenes;

        // Get the original first and last word of each scene
        // This will be used to calculate the new scene times
        const interestPoints = call(
            calculateInterestPointTimesForEntireSceneGroupOperation,
            sceneGroupId,
        );
        const firstAndLastWordIdsBySceneId = editor
            .getEditorState()
            .read(() => $getFirstLastWordIdsBySceneId(scenes));

        const leftTrimmedOutWordId = baseAssetStartTime
            ? editor.getEditorState().read(() => {
                  const firstTrim = $findMatchWithin($getRoot(), $isTrimElementNode);
                  if (!firstTrim) {
                      return undefined;
                  }
                  const wordNode = $getLastNonEmptyWordWithin(firstTrim);
                  return wordNode?.id;
              })
            : undefined;

        // Everything below this point is a mutation, so work out (and validate) the whole
        // reconciliation first. A regeneration we can't fully apply must leave the scene group
        // untouched rather than half re-timed.
        const plan = planRegeneratedVoiceoverReconciliation({
            sceneGroup,
            newTranscriptWords: transcriptResult,
            audioAssetSourceData,
            audioAssetDurationMillis,
            firstAndLastWordIdsBySceneId,
            baseAssetStartTime,
            hasEndTrim,
            leftTrimmedOutWordId,
        });

        await updateEditor(editor, () => {
            // Next, rebuild the transcript. Note: the new transcript words will have the same ids
            const silenceNodeGenerationMethod =
                typeOfTranscript === TypeOfTranscript.MEDIA_TRANSCRIPT_TAB
                    ? SilenceNodeGenerationMethod.GenerateFromTranscriptWord
                    : SilenceNodeGenerationMethod.DoNotGenerate;
            $generateEditorFromTranscript(
                transcriptResult,
                shouldSeparateTranscriptWordsWithSpaces(getState(), sceneGroupId, typeOfTranscript),
                silenceNodeGenerationMethod,
            );
        });

        // Update luminary. The interest points were captured above (before the transcript was
        // rebuilt), so pass them through rather than letting the wrapper recapture the moved words.
        call(
            runVoicedrivenTranscriptBatchWithPrecapturedInterestPointsOperation,
            sceneGroupId,
            () => {
                sceneGroup.voiceoverAsset.sourceData = plan.audioAssetSourceData;

                for (const { scene, duration } of plan.sceneDurations) {
                    setVoicedrivenSceneDuration(scene, duration);
                }

                if (plan.newStartTrimTime !== undefined) {
                    setVoiceDrivenBaseAssetStartTime(sceneGroup, plan.newStartTrimTime);
                }
                if (plan.newEndTrimTime !== undefined) {
                    setVoiceDrivenBaseAssetEndTime(sceneGroup, plan.newEndTrimTime);
                }
            },
            interestPoints,
        );

        call(
            saveTranscriptOperation,
            sceneGroupId,
            editor,
            { waitForLuminary: false },
            systemTriggered,
            parentActionGroup,
        );
    },
);

// Wrapper for editAIVoiceoverOperation that selects the entire transcript for edit
export const editFullAIVoiceoverOperation = createLuminaryOperation(
    async (
        { luminary, dispatch, getState, call },
        sceneGroupId: string,
        newVoiceSpeed: number | null = null,
        selectedAIVoiceId?: string,
        systemTriggered: boolean = false,
        parentActionGroup: string = uuid(),
    ) => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) {
            return;
        }
        const originalVoiceoverAssetSourceData = getVoiceDrivenAssetSourceData(
            sceneGroup,
        ) as AudioAssetSourceData;
        if (!originalVoiceoverAssetSourceData) {
            return;
        }

        const editor = getEditorFromSceneGroupId(getState(), sceneGroupId);
        if (!editor) {
            return;
        }
        const userSelectedWordIds = undefined; // Pass undefined to select all words
        const fullTranscript = getFullTranscriptFromEditor(editor, userSelectedWordIds, {
            includeSilence: true,
        });
        const langCode = getOriginalTranscriptLanguage(getState(), sceneGroupId);
        await call(
            editAIVoiceoverOperation,
            {
                newVoice: selectedAIVoiceId,
                newVoiceSpeed: newVoiceSpeed,
                sceneGroupId,
                transcript: fullTranscript,
                langCode,
                originalVoiceoverAssetSourceData,
            },
            systemTriggered,
            parentActionGroup,
        );
    },
);

export function translateTranscript(
    targetLanguage: LanguageLocalesEnum,
    sceneGroupId: string,
    videoId: number | null,
) {
    return async (dispatch: AppDispatch, getState: GetRootState) => {
        const editor = getEditorFromSceneGroupId(getState(), sceneGroupId);
        const originalTranscriptLanguage = getOriginalTranscriptLanguage(getState(), sceneGroupId);
        if (!editor) {
            throw new Error('You must have an editor to translate the transcript!');
        }
        const supportedLanguages = getSupportedLanguages(getState());
        const parentLanguage = convertLocaleToLanguageCode(supportedLanguages, targetLanguage);
        if (!parentLanguage) {
            throw new Error('Target language is not supported');
        }
        const transcript = getFullTranscriptFromEditor(editor);
        try {
            const translatedTranscript = await translateText(
                transcript,
                targetLanguage,
                originalTranscriptLanguage,
                videoId,
            );

            // use the editor instead of editorState
            const translatedTranscriptEditor = await dispatch(
                buildTranslatedTranscript(translatedTranscript, parentLanguage),
            );

            dispatch(
                storeTranslatedEditor({
                    sceneGroupId,
                    editor: translatedTranscriptEditor,
                    langCode: parentLanguage,
                    lexicalControlsItsOwnState: false,
                }),
            );
        } catch (err) {
            const error = isErrorObject(err) ? err.error : err;
            const message = error instanceof Error ? error.message : undefined;
            Toast.error(message || 'Error translating transcript. Please try again.');
        }
    };
}

export const applyTranslatedCaptionTranscriptOperation = createLuminaryOperation(
    async (
        { luminary, dispatch, getState, call },
        sceneGroupId: string,
        translatedLanguage: LanguageEnum,
        parentActionGroup: string = uuid(),
        systemTriggered: boolean = false,
    ) => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) {
            return;
        }
        if (isAIVoiceoverSceneGroup(sceneGroup)) {
            // This operation only swaps caption text; it does not regenerate the AI voiceover audio
            // to match the translated script. AI voiceover scene groups must go through
            // applyTranslatedCaptionTranscriptV2Operation, which re-synthesizes the audio.
            throw new Error(
                'applyTranslatedCaptionTranscriptOperation does not support AI voiceover scene groups (it cannot regenerate the voiceover audio); use applyTranslatedCaptionTranscriptV2Operation instead.',
            );
        }
        const translatedTranscriptEditor = getTranslatedEditorBySceneGroupId(
            getState(),
            sceneGroup.id,
        );
        if (!translatedTranscriptEditor) throw new Error('Translated transcript editor not found');

        // store the translation into redux
        dispatch(
            storeTranscriptTranslationsFromEditor(
                translatedTranscriptEditor,
                translatedLanguage,
                sceneGroup.id,
                systemTriggered,
                parentActionGroup,
            ),
        );

        // sync the translation transcript back to luminary
        call(
            syncTranscriptToLuminaryOperation,
            translatedTranscriptEditor,
            sceneGroup.id,
            TypeOfTranscript.TRANSLATION,
        );
    },
);

/**
 * V2 of applyTranslatedCaptionTranscriptOperation.
 *
 * For AI voiceover scene groups, instead of delegating to editAIVoiceoverOperation
 * (which rebalances scenes by original duration percentages), this operation:
 * 1. Calls editVoiceover API directly to get ElevenLabs timed words + audio
 * 2. Matches timed words to the LLM's per-scene caption splits by word count
 * 3. Sets scene boundaries at the exact timestamps where the per-scene splits fall
 *
 * This preserves the LLM's carefully chosen sentence boundaries rather than
 * re-splitting proportionally.
 */
export const applyTranslatedCaptionTranscriptV2Operation = createLuminaryOperation(
    async (
        { luminary, dispatch, getState, call },
        sceneGroupId: string,
        translatedLanguage: LanguageEnum,
        segments: string[],
        parentActionGroup: string = uuid(),
        systemTriggered: boolean = false,
    ) => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) {
            return;
        }
        const isAIVoiceover = isAIVoiceoverSceneGroup(sceneGroup);
        const translatedTranscriptEditor = getTranslatedEditorBySceneGroupId(
            getState(),
            sceneGroup.id,
        );
        if (!translatedTranscriptEditor) throw new Error('Translated transcript editor not found');

        if (isAIVoiceover) {
            const transcript = getFullTranscriptFromEditor(translatedTranscriptEditor);

            dispatch(
                markSceneGroupAsLoading({
                    sceneGroupId,
                    type: SCENE_GROUP_LOADING_TYPES.RECOMMENDING_OVERLAYS,
                    context: { loadingMsg: LOADING_SCENE_MSG.AI_VOICEOVER_EDIT },
                }),
            );

            try {
                const ws = WebSocketClient.getInstance();
                const response = await ApiService.editVoiceoverUpdate({
                    body: {
                        full_transcript: transcript,
                        language: translatedLanguage,
                        original_audio_asset_source_data: sceneGroup.voiceoverAsset
                            ?.sourceData as unknown as AudioAssetSourceData,
                        trim_end_millis: getVoiceDrivenBaseAssetEndTime(sceneGroup),
                        trim_start_millis: getVoiceDrivenBaseAssetStartTime(sceneGroup),
                        context_select_full_transcript: true,
                        video_id: getVideoId(getState()),
                        segments: segments,
                    },
                });
                const result = await ws.waitForMessage<EditVoiceoverTaskResponse>(
                    response.data.async_operation_uuid,
                    AsyncOperationMessageTypeEnum.ASYNC_OPERATION_RESULT,
                    AsyncOperationNameEnum.GENERATE_EDITED_VOICEOVER,
                );

                const transcriptResult = convertTranscriptWordsToMillis(result.transcript);
                const audioAssetDurationMillis = Math.max(
                    getTranscriptDuration(transcriptResult),
                    result.audio_asset.duration * 1000.0,
                );
                const audioAssetSourceData = {
                    ...result.audio_asset,
                    duration: audioAssetDurationMillis / 1000.0,
                };

                dispatch(startLexicalControllingItsOwnState({ sceneGroupId }));

                const mainEditor = getEditorFromSceneGroupId(getState(), sceneGroupId);
                if (!mainEditor) {
                    throw new Error('Main editor not found for scene group');
                }

                const useSpaces = shouldSeparateTranscriptWordsWithSpaces(
                    getState(),
                    sceneGroupId,
                    TypeOfTranscript.TRANSLATION,
                );

                // Backend returns boundary times in seconds via TranscriptAligner;
                // convert to millis to match scene duration units
                const sceneBoundaryTimesMillis = (result.scene_boundary_times ?? []).map(
                    (t) => t * 1000,
                );

                await updateEditor(mainEditor, () => {
                    $generateEditorFromTranscript(
                        transcriptResult,
                        useSpaces,
                        SilenceNodeGenerationMethod.DoNotGenerate,
                    );

                    call(runVoicedrivenTranscriptBatchOperation, sceneGroupId, () => {
                        sceneGroup.voiceoverAsset.sourceData = audioAssetSourceData;

                        // Use the transcript end time as total duration rather than
                        // audioAssetDurationMillis, which may exceed the loaded asset duration and
                        // cause negative adjustments later.
                        const totalDuration =
                            transcriptResult.length > 0
                                ? transcriptResult[transcriptResult.length - 1].endTime
                                : audioAssetDurationMillis;

                        let timeUsed = 0;
                        const sceneCount = sceneGroup.scenes.length;
                        const sceneIdsToRemove: string[] = [];
                        sceneGroup.scenes.forEach((scene, i) => {
                            const isLastScene = i === sceneCount - 1;
                            let boundaryTime = sceneBoundaryTimesMillis[i];

                            if (isLastScene || boundaryTime === undefined) {
                                boundaryTime = totalDuration;
                            }

                            const duration = boundaryTime - timeUsed;
                            if (duration <= 0) {
                                sceneIdsToRemove.push(scene.id);
                                return;
                            }

                            setVoicedrivenSceneDuration(scene, duration);
                            timeUsed = boundaryTime;
                        });
                        sceneIdsToRemove.forEach((id) => sceneGroup.removeScene(id));
                    });
                });

                call(
                    saveTranscriptOperation,
                    sceneGroupId,
                    mainEditor,
                    { waitForLuminary: false },
                    systemTriggered,
                    parentActionGroup,
                );

                await call(updateSceneGroupDurationToEqualVoiceDrivenAssetDurationOperation, {
                    sceneGroup,
                });
            } catch (e) {
                dispatch(endLexicalControllingItsOwnState({ sceneGroupId }));
                dispatch(markSceneGroupAsFinishedLoading({ sceneGroupId }));
                call(resetLuminaryAndLexicalToPresentCheckpointOperation);
                throw e;
            }

            dispatch(endLexicalControllingItsOwnState({ sceneGroupId }));
            dispatch(markSceneGroupAsFinishedLoading({ sceneGroupId }));
            dispatch(removeTranslatedEditor({ sceneGroupId }));
            dispatch(
                setTranscriptLanguage({
                    sceneGroupId,
                    language: translatedLanguage,
                    systemTriggered,
                    parentActionGroup,
                }),
            );
            call(
                checkpointVideoAndTranscriptOperation,
                sceneGroupId,
                { waitForLuminary: true },
                systemTriggered,
                parentActionGroup,
            );
        } else {
            // Talking head / human VO: same as v1 — no scene boundary issue
            dispatch(
                storeTranscriptTranslationsFromEditor(
                    translatedTranscriptEditor,
                    translatedLanguage,
                    sceneGroup.id,
                    systemTriggered,
                    parentActionGroup,
                ),
            );

            call(
                syncTranscriptToLuminaryOperation,
                translatedTranscriptEditor,
                sceneGroup.id,
                TypeOfTranscript.TRANSLATION,
            );
        }
    },
);

export const cutSilencesOperation = createLuminaryOperation(
    (
        { getState, call, luminary },
        sceneGroupId: string,
        minCuttableSilenceDuration: number,
        systemTriggered: boolean,
        parentActionGroup: string,
    ): number => {
        const editor = getEditorFromSceneGroupId(getState(), sceneGroupId);
        if (!editor) {
            return 0;
        }
        return editor.getEditorState().read(() => {
            const silenceSpans = $getSilenceSpans();

            let cumulativeCutDuration = 0;
            luminary.batchRun(() => {
                silenceSpans.forEach(([beginningOfSilence, endOfSilence]) => {
                    const { startTime, endTime } = call(
                        $getTimeIndicatedBy,
                        beginningOfSilence,
                        endOfSilence,
                        sceneGroupId,
                    );
                    const cutDuration = endTime - startTime;
                    if (cutDuration < minCuttableSilenceDuration) {
                        // if the cut duration is less than the minimum, we don't cut it
                        return;
                    }
                    cumulativeCutDuration += cutDuration;
                    call(
                        cutVoicedrivenSceneGroupOperation,
                        sceneGroupId,
                        startTime,
                        endTime,
                        systemTriggered,
                        parentActionGroup,
                    );
                });
            });
            return cumulativeCutDuration;
        });
    },
);

export const $getFillerWordCutBoundaries = createLuminaryOperation(
    (
        { luminary },
        startNode: TranscriptWordNode,
        endNode: TranscriptWordNode,
        sceneGroupId: string,
    ) => {
        // Skip past adjacent filler words to find the nearest non-filler neighbor.
        // This ensures consecutive fillers all compute boundaries relative to the
        // same non-filler words, producing overlapping cuts that merge correctly.
        let wordBeforeFirstSelectedWord = $getPreviousTranscriptWordNode(startNode, {
            includeSpaces: false,
            includeTrimmedWords: false,
        });
        while (wordBeforeFirstSelectedWord?.filler) {
            wordBeforeFirstSelectedWord = $getPreviousTranscriptWordNode(
                wordBeforeFirstSelectedWord,
                { includeSpaces: false, includeTrimmedWords: false },
            );
        }
        let wordAfterLastSelectedWord = $getNextTranscriptWordNode(endNode, {
            includeSpaces: false,
            includeTrimmedWords: false,
        });
        while (wordAfterLastSelectedWord?.filler) {
            wordAfterLastSelectedWord = $getNextTranscriptWordNode(wordAfterLastSelectedWord, {
                includeSpaces: false,
                includeTrimmedWords: false,
            });
        }

        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) {
            throw new Error('Scene group missing from luminary!');
        }
        const voiceDrivenAssetPlaybackRegions = getVoiceDrivenAssetPlaybackRegions(sceneGroup);
        const voiceDrivenAssetSourceDuration = getVoiceDrivenBaseAssetSourceDuration(sceneGroup);
        const preExistingCuts = getVoiceDrivenBaseAssetCutRegions(sceneGroup);

        const sceneStartTimes = sceneGroup.scenes.map((scene) => {
            return translateVideoTimeToTranscriptTime(
                getVoicedrivenSceneStartTime(scene),
                voiceDrivenAssetPlaybackRegions,
                voiceDrivenAssetSourceDuration,
                sceneGroup.startTime,
            );
        });

        let startTime = 0;
        if (wordBeforeFirstSelectedWord) {
            const sceneStartTimeBetween = sceneStartTimes.find((sceneStartTime) => {
                return thisIsWithinRange(sceneStartTime, [
                    wordBeforeFirstSelectedWord.endTime,
                    startNode.startTime,
                ]);
            });
            const preExistingCutBetween = preExistingCuts.find(([startTime, endTime]) => {
                return theseAreOverlapping(
                    wordBeforeFirstSelectedWord.endTime,
                    startNode.startTime,
                    startTime,
                    endTime,
                );
            });
            if (sceneStartTimeBetween) {
                startTime = sceneStartTimeBetween;
            } else if (preExistingCutBetween) {
                startTime = preExistingCutBetween[1];
            } else {
                startTime = wordBeforeFirstSelectedWord.endTime + FILLER_ADJACENT_WORD_GUARD_MS;
            }
        }

        let endTime = voiceDrivenAssetSourceDuration;
        if (wordAfterLastSelectedWord) {
            const sceneStartTimeBetween = sceneStartTimes.find((sceneStartTime) => {
                return thisIsWithinRange(sceneStartTime, [
                    endNode.endTime,
                    wordAfterLastSelectedWord.startTime,
                ]);
            });
            const preExistingCutBetween = preExistingCuts.find(([startTime, endTime]) => {
                return theseAreOverlapping(
                    endNode.endTime,
                    wordAfterLastSelectedWord.startTime,
                    startTime,
                    endTime,
                );
            });
            if (sceneStartTimeBetween) {
                endTime = sceneStartTimeBetween;
            } else if (preExistingCutBetween) {
                endTime = preExistingCutBetween[0];
            } else {
                endTime = wordAfterLastSelectedWord.startTime - FILLER_ADJACENT_WORD_GUARD_MS;
            }
        }

        return { startTime, endTime };
    },
);

export const cutFillerWordsOperation = createLuminaryOperation(
    (
        { getState, luminary, call },
        sceneGroupId: string,
        systemTriggered: boolean,
        parentActionGroup: string,
    ) => {
        const editor = getEditorFromSceneGroupId(getState(), sceneGroupId);
        if (!editor) {
            return 0;
        }
        return editor.getEditorState().read(() => {
            const fillerWords = $getUntrimmedFillerWords();
            if (fillerWords.length === 0) {
                return 0;
            }

            luminary.batchRun(() => {
                fillerWords.forEach((fillerWord) => {
                    const { startTime, endTime } = call(
                        $getFillerWordCutBoundaries,
                        fillerWord,
                        fillerWord,
                        sceneGroupId,
                    );
                    if (endTime <= startTime) {
                        return;
                    }
                    call(
                        cutVoicedrivenSceneGroupOperation,
                        sceneGroupId,
                        startTime,
                        endTime,
                        systemTriggered,
                        parentActionGroup,
                    );
                });
            });
            return fillerWords.length;
        });
    },
);

export const enterEditTimingModeOperation = createLuminaryOperation(
    (
        { getState, luminary, dispatch },
        {
            assetId,
            sceneId,
            sceneGroupId,
        }: {
            assetId: string;
            sceneId: string;
            sceneGroupId: string;
        },
    ) => {
        const editor = getEditorFromSceneGroupId(getState(), sceneGroupId);
        if (!editor) {
            throw new Error('Editor not found');
        }

        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) {
            throw new Error('Scene group not found');
        }
        const scene = luminary.getSceneById(sceneId);
        if (!scene) {
            throw new Error('Scene not found');
        }
        const asset = scene.getAssetById(assetId);
        if (!asset) {
            throw new Error('Asset not found');
        }
        dispatch(startLexicalControllingItsOwnState({ sceneGroupId }));
        dispatch(startTimingMode({ assetId, sceneId, sceneGroupId }));

        editor.dispatchCommand(ENTER_TIMING_MODE_COMMAND, undefined);
    },
);

export const exitEditTimingModeOperation = createLuminaryOperation(
    ({ getState, luminary, dispatch }) => {
        const sceneGroupId = getTimingModeSceneGroupId(getState());
        if (!sceneGroupId) {
            return;
        }

        if (!isTimingModeActive(getState())) {
            return;
        }
        const editor = getEditorFromSceneGroupId(getState(), sceneGroupId);
        if (!editor) {
            return;
        }

        dispatch(endTimingMode());
        editor.dispatchCommand(EXIT_TIMING_MODE_COMMAND, undefined);
        dispatch(endLexicalControllingItsOwnState({ sceneGroupId }));
    },
);

export const calculateInterestPointTimesForEntireSceneGroupOperation = createLuminaryOperation(
    ({ luminary, getState, call }, sceneGroupId: string): InterestPointTime[] => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) {
            return [];
        }
        return sceneGroup.scenes.flatMap((scene) => {
            return call(calculateInterestPointTimesOperation, scene.id);
        });
    },
);

/**
 * Calculates interest points for assets with user-overridden timing in a scene.
 * Interest points represent where asset intro/outro times are relative to transcript words.
 */
export const calculateInterestPointTimesOperation = createLuminaryOperation(
    ({ luminary, getState }, sceneId: string): InterestPointTime[] => {
        const scene = luminary.getSceneById(sceneId);
        if (!scene) {
            return [];
        }

        const sceneGroup = scene.sceneGroup;
        if (!sceneGroup) {
            return [];
        }
        const assetsWithOverriddenTiming = getAssetsWithTimesOverridden(getState(), sceneId);
        if (assetsWithOverriddenTiming.length === 0) {
            return [];
        }

        const editor = getEditorFromSceneGroupId(getState(), sceneGroup.id);
        if (!editor) {
            return [];
        }

        const voiceDrivenAssetPlaybackRegions = getVoiceDrivenAssetPlaybackRegions(sceneGroup);
        const voiceDrivenAssetSourceDuration = getVoiceDrivenBaseAssetSourceDuration(sceneGroup);
        const sceneGroupStartTime = sceneGroup.startTime;

        const interestPoints: InterestPointTime[] = [];

        return editor.getEditorState().read(() => {
            for (const assetId of assetsWithOverriddenTiming) {
                const asset = scene.getAssetById(assetId);
                if (!asset) {
                    continue;
                }

                const assetLuminaryParent = asset.luminaryParent;
                if (!assetLuminaryParent) {
                    continue;
                }

                const assetStartTimeVideoTime =
                    assetLuminaryParent.startTime + asset.introAnimationOffset;
                const assetEndTimeVideoTime =
                    assetLuminaryParent.endTime - asset.outroAnimationOffset;

                const introTimeTranscript = translateVideoTimeToTranscriptTime(
                    assetStartTimeVideoTime,
                    voiceDrivenAssetPlaybackRegions,
                    voiceDrivenAssetSourceDuration,
                    sceneGroupStartTime,
                );
                const outroTimeTranscript = translateVideoTimeToTranscriptTime(
                    assetEndTimeVideoTime,
                    voiceDrivenAssetPlaybackRegions,
                    voiceDrivenAssetSourceDuration,
                    sceneGroupStartTime,
                );

                const prevWordForIntro = $getPreviousWordAtTime(introTimeTranscript);
                const nextWordForIntro = $getNextWordAtTime(introTimeTranscript);

                const shouldCreateInterestPoint = (
                    prevWord: TranscriptWordNode,
                    nextWord: TranscriptWordNode,
                    offset: number,
                ) => {
                    const prevWordOverlay = $getNearestNodeOfType(prevWord, BaseOverlayElementNode);
                    const nextWordOverlay = $getNearestNodeOfType(nextWord, BaseOverlayElementNode);
                    if (prevWordOverlay && nextWordOverlay && prevWordOverlay === nextWordOverlay) {
                        // the point is between two words in a single overlay. Definitely an interest point.
                        return true;
                    }
                    // the point is next to an overlay boundary. It could be an interest point, or maybe the scene design has
                    // an offset and we should not override it (eg. the user changed the intro animation offset, but not
                    // the outro animation offset for this asset). Only create an interest point if the offset is large, which
                    // means it's more likely that the user has explicitly set the offset.
                    return offset > 300;
                };
                if (prevWordForIntro && nextWordForIntro) {
                    if (
                        shouldCreateInterestPoint(
                            prevWordForIntro,
                            nextWordForIntro,
                            asset.introAnimationOffset,
                        )
                    ) {
                        const prevWordIdForIntro = prevWordForIntro.id;
                        const timeOffsetForIntro = introTimeTranscript - prevWordForIntro.endTime;

                        interestPoints.push({
                            timeType: InterestPointTimeType.AssetIntro,
                            sceneId,
                            assetId,
                            prevWordId: prevWordIdForIntro,
                            timeOffset: timeOffsetForIntro,
                        });
                    }
                }

                const prevWordForOutro = $getPreviousWordAtTime(outroTimeTranscript);
                const nextWordForOutro = $getNextWordAtTime(outroTimeTranscript);

                if (prevWordForOutro && nextWordForOutro) {
                    if (
                        shouldCreateInterestPoint(
                            prevWordForOutro,
                            nextWordForOutro,
                            asset.outroAnimationOffset,
                        )
                    ) {
                        const prevWordIdForOutro = prevWordForOutro.id;
                        const timeOffsetForOutro = outroTimeTranscript - prevWordForOutro.endTime;

                        interestPoints.push({
                            timeType: InterestPointTimeType.AssetOutro,
                            sceneId,
                            assetId,
                            prevWordId: prevWordIdForOutro,
                            timeOffset: timeOffsetForOutro,
                        });
                    }
                }
            }

            return interestPoints;
        });
    },
);

/**
 * Reapplies interest points after a transformation (e.g., overlay translation).
 * Finds the word nodes by ID and recalculates asset times based on the new word positions.
 *
 * If you are inside of an editor.update() block, it's better to use $reapplyInterestPoints directly
 * because it will get the most up-to-date editor state.
 */
export const reapplyInterestPointsOperation = createLuminaryOperation(
    ({ luminary, getState, call }, interestPoints: InterestPointTime[], sceneGroupId: string) => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) {
            return;
        }

        const editor = getEditorFromSceneGroupId(getState(), sceneGroupId);
        if (!editor) {
            return;
        }

        return editor.getEditorState().read(() => {
            call($reapplyInterestPoints, interestPoints, sceneGroupId);
        });
    },
);

type AssetTimingContainer = { startTime: number; endTime: number; duration: number };

/**
 * Rescues a span whose anchor word fell out of its container - an asset timed to the last word of
 * an overlay still points at that word after the overlay is shortened past it, which clamps the
 * asset's intro to the very end of the container so it never shows. When the reapplied span no
 * longer holds any of the container's words, snap it onto the closest one that is still there.
 */
const $keepTimingOverAWord = (
    specification: TimingSpecification,
    container: AssetTimingContainer,
    sceneGroup: SceneGroup,
): TimingSpecification => {
    const playbackRegions = getVoiceDrivenAssetPlaybackRegions(sceneGroup);
    const sourceDuration = getVoiceDrivenBaseAssetSourceDuration(sceneGroup);
    const toTranscriptTime = (containerTime: number, opts?: { includeTrailingCut: boolean }) =>
        translateVideoTimeToTranscriptTime(
            container.startTime + containerTime,
            playbackRegions,
            sourceDuration,
            sceneGroup.startTime,
            opts,
        );
    // An end boundary sitting on a cut is both the cut's start and its end. Take the start, so the
    // span is not read as reaching over the cut and into words that are not in the narration.
    const toEndTranscriptTime = (containerTime: number) =>
        toTranscriptTime(containerTime, { includeTrailingCut: false });
    const toContainerTime = (transcriptTime: number) =>
        Math.min(
            Math.max(
                0,
                translateTranscriptTimeToVideoTime(
                    transcriptTime,
                    playbackRegions,
                    sourceDuration,
                    sceneGroup.startTime,
                ) - container.startTime,
            ),
            container.duration,
        );

    const words = $getWordNodesBetweenTimes(
        toTranscriptTime(0),
        toEndTranscriptTime(container.duration),
    );
    if (words.length === 0) {
        return specification;
    }
    const introTranscriptTime = toTranscriptTime(specification.introTime);
    const exitTranscriptTime = toEndTranscriptTime(specification.exitTime);
    const holdsAWord = words.some(
        (word) => word.startTime >= introTranscriptTime && word.endTime <= exitTranscriptTime,
    );
    if (holdsAWord) {
        return specification;
    }

    const lastWord = words[words.length - 1];
    const wordToCover = introTranscriptTime > lastWord.startTime ? lastWord : words[0];
    const idealBoundaryTimeCloseTo = (transcriptTime: number) =>
        $getFineOverlayBoundaryTimeCloseTo(transcriptTime, playbackRegions, sourceDuration, {
            getIdealOverlayBoundaryTime: true,
        });
    return {
        ...specification,
        introTime: Math.min(
            specification.introTime,
            toContainerTime(idealBoundaryTimeCloseTo(wordToCover.startTime)),
        ),
        exitTime: Math.max(
            specification.exitTime,
            toContainerTime(idealBoundaryTimeCloseTo(wordToCover.endTime)),
        ),
    };
};

export const $reapplyInterestPoints = createLuminaryOperation(
    ({ luminary, call }, interestPoints: InterestPointTime[], sceneGroupId: string) => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) {
            return;
        }

        const voiceDrivenAssetPlaybackRegions = getVoiceDrivenAssetPlaybackRegions(sceneGroup);
        const voiceDrivenAssetSourceDuration = getVoiceDrivenBaseAssetSourceDuration(sceneGroup);

        luminary.batchRun(() => {
            // create a single timing specification for each asset
            const specsByAssetKey = new Map<string, TimingSpecification>();
            const assetKeysWithAnchorOutsideContainer = new Set<string>();
            for (const interestPoint of interestPoints) {
                const scene = luminary.getSceneById(interestPoint.sceneId);
                if (!scene) {
                    continue;
                }

                const asset = scene.getAssetById(interestPoint.assetId);
                if (!asset) {
                    continue;
                }

                const assetLuminaryParent = asset.luminaryParent;
                if (!assetLuminaryParent) {
                    continue;
                }

                let newTimeTranscript: number;
                if (interestPoint.prevWordId === null) {
                    // Before the first word, so the intereset point is relative to the start of the transcript
                    newTimeTranscript = interestPoint.timeOffset;
                } else {
                    const prevWord = $getWordNodeByWordId(interestPoint.prevWordId);
                    if (!prevWord) {
                        // Word not found - it might have been deleted or something. Skip this interest point.
                        continue;
                    }
                    newTimeTranscript = prevWord.endTime + interestPoint.timeOffset;
                }

                const newTimeVideo = translateTranscriptTimeToVideoTime(
                    newTimeTranscript,
                    voiceDrivenAssetPlaybackRegions,
                    voiceDrivenAssetSourceDuration,
                    sceneGroup.startTime,
                );

                const newTimeContainerRelative = newTimeVideo - assetLuminaryParent.startTime;
                const newTimeSceneRelative = Math.min(
                    Math.max(0, newTimeContainerRelative),
                    assetLuminaryParent.duration,
                );

                const specKey = `${interestPoint.sceneId}:${interestPoint.assetId}`;
                if (newTimeContainerRelative !== newTimeSceneRelative) {
                    // The anchor word is no longer inside the container - whatever moved (the
                    // overlay's boundary, a cut, the transcript itself) left it behind.
                    assetKeysWithAnchorOutsideContainer.add(specKey);
                }
                const spec = specsByAssetKey.get(specKey) ?? {
                    sceneId: interestPoint.sceneId,
                    assetId: interestPoint.assetId,
                    introTime: asset.introAnimationOffset,
                    exitTime: assetLuminaryParent.duration - asset.outroAnimationOffset,
                };

                // Apply the new time based on timeType
                switch (interestPoint.timeType) {
                    case InterestPointTimeType.AssetIntro:
                        spec.introTime = newTimeSceneRelative;
                        break;
                    case InterestPointTimeType.AssetOutro:
                        spec.exitTime = newTimeSceneRelative;
                        break;
                }

                specsByAssetKey.set(specKey, spec);
            }

            const timingSpecifications = Array.from(specsByAssetKey.entries()).map(
                ([specKey, specification]) => {
                    if (!assetKeysWithAnchorOutsideContainer.has(specKey)) {
                        return specification;
                    }
                    const container = luminary
                        .getSceneById(specification.sceneId)
                        ?.getAssetById(specification.assetId)?.luminaryParent;
                    if (!container) {
                        return specification;
                    }
                    return $keepTimingOverAWord(specification, container, sceneGroup);
                },
            );

            call(setAssetIntroExitTimesOperation, {
                timingSpecifications,
                operatingOnMainLuminary: true,
                systemTriggered: true,
                parentActionGroup: uuid(),
            });
        });
    },
);
